import { CheckCircle } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import {
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  InputProps,
  TextField,
  useTheme,
} from '@mui/material'
import React, {
  createContext,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

import type { StyledPaletteColors } from '../../utils/getColor.ts'
import { ContentConfirmMessage } from '../components/Dialog/ContentConfirmMessage.tsx'

// --- DEFINICIONES DE TIPO EMPRESARIALES ---
export interface AppConfirmResult {
  confirmed: boolean // Más explícito que 'confirmed'
  reason?: string
}

export interface AppConfirmOptions {
  title?: string
  dialogTitle?: string
  // Descripción del mensaje
  description?: string | ReactNode
  // Acepta Array de strings o de Componentes heredado de ContentConfirmMessage
  steps?: (string | ReactNode)[]
  // En caso se quiera customizar
  content?: ReactNode
  confirmationText?: string
  cancellationText?: string
  // Color temático basado en la paleta de colores de la aplicación (default: 'primary')
  color?: StyledPaletteColors
  confirmButtonColor?: Exclude<ButtonProps['color'], 'inherit'>
  confirmButtonProps?: Partial<ButtonProps>
  cancellationButtonProps?: Partial<ButtonProps>
  confirmButtonIcon?: ReactNode
  // -- Propiedades del Input --
  showInput?: boolean // Mostrar el input?
  inputRequired?: boolean // Es obligatorio? (Valida y pone *)
  inputLabel?: string // Label personalizado
  inputPlaceholder?: string // Placeholder personalizado
  inputValue?: string // Valor inicial (opcional)
  inputHelperText?: string // Helper text
  inputProps?: InputProps // Propiedades adicionales del input

  // Deshabilita el esc y backdrop
  disableCloseOnOverlay?: boolean // Seguridad extra
  maxWidth?: DialogProps['maxWidth']
  fullWidth?: boolean
}

// Props del Provider (Configuración Global)
interface AppConfirmProviderProps {
  children: ReactNode
  defaultOptions?: Partial<AppConfirmOptions> // Configuración base
}

export interface AppConfirmContextType {
  requestConfirm: (options?: AppConfirmOptions) => Promise<AppConfirmResult>
  confirm: (options?: AppConfirmOptions) => Promise<AppConfirmResult>
  closeConfirm: () => void
}

/**
 * Diálogo de confirmación interno, optimizado para evitar re-renders y lag de entrada
 */
const ConfirmDialogInternal: React.FC<{
  open: boolean
  options: AppConfirmOptions
  onClose: () => void
  onConfirm: (reason: string) => void
}> = memo(({ open, options, onClose, onConfirm }) => {
  const [reasonValue, setReasonValue] = useState('')
  const [reasonError, setReasonError] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const theme = useTheme()

  const { steps = [] } = options

  // Efecto para inicializar valores cuando se abre el modal
  useEffect(() => {
    if (open) {
      setReasonValue(options.inputValue || '')
      setReasonError(false)
      setIsSubmitting(false)
    }
  }, [open, options.inputValue])

  // Color de confirmación: color (StyledPaletteColors) > confirmButtonColor > 'primary'
  const confirmColor: StyledPaletteColors =
    options.color || (options.confirmButtonColor as StyledPaletteColors) || 'primary'

  // Evento click del botón y sus respectivas validaciones
  const handleConfirmClick = () => {
    if (isSubmitting) return
    const trimmed = reasonValue.trim()
    if (options.inputRequired && !trimmed) {
      setReasonError(true)
      return
    }
    setIsSubmitting(true)
    onConfirm(trimmed)
  }

  // Helper de visualización
  const shouldShowInput = options.showInput || options.inputRequired

  return (
    <Dialog
      open={open}
      maxWidth={options.maxWidth || 'sm'}
      fullWidth={options.fullWidth ?? true}
      onClose={(_e, reason) => {
        if (
          options.disableCloseOnOverlay &&
          (reason === 'backdropClick' || reason === 'escapeKeyDown')
        )
          return
        if (isSubmitting) return
        onClose()
      }}
      slotProps={{
        paper: {
          sx: {
            borderRadius: 2.5,
            m: { xs: 2, sm: 3 },
            boxShadow: theme.shadows[12],
          },
        },
      }}
    >
      <DialogTitle sx={{ py: 1.7, fontWeight: 800 }}>
        {options.dialogTitle || 'Autorización'}
      </DialogTitle>

      {!options.disableCloseOnOverlay && (
        <IconButton
          aria-label="Cerrar"
          tabIndex={-1}
          disabled={isSubmitting}
          onClick={onClose}
          sx={(th) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: th.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
      )}

      <DialogContent dividers>
        <Box sx={{ mb: shouldShowInput ? 2 : 0 }}>
          {typeof options.content === 'string' || !options.content ? (
            <ContentConfirmMessage
              title={options.title || 'Confirmación'}
              description={
                options.description ||
                (typeof options.content === 'string' ? options.content : undefined)
              }
              type={confirmColor}
              steps={steps}
            />
          ) : (
            options.content
          )}
        </Box>

        {shouldShowInput && (
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            variant="outlined"
            required={options.inputRequired}
            disabled={isSubmitting}
            label={
              options.inputLabel ||
              (options.inputRequired ? 'Motivo (Obligatorio)' : 'Comentarios')
            }
            placeholder={
              options.inputPlaceholder || 'Escriba aquí... (Ctrl + Enter para confirmar)'
            }
            value={reasonValue}
            onChange={(e) => {
              setReasonValue(e.target.value)
              if (options.inputRequired && e.target.value.trim()) {
                setReasonError(false)
              }
            }}
            onKeyDown={(e) => {
              if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
                e.preventDefault()
                handleConfirmClick()
              }
            }}
            error={reasonError}
            helperText={
              reasonError
                ? 'Este campo es obligatorio para continuar.'
                : options.inputHelperText || ''
            }
            slotProps={{
              input: options.inputProps,
            }}
            sx={{ mt: 1 }}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ py: 1.7, px: 3, justifyContent: 'center', gap: 1 }}>
        <Button
          onClick={onClose}
          color="inherit"
          variant="text"
          disabled={isSubmitting}
          {...options.cancellationButtonProps}
        >
          {options.cancellationText || 'Cancelar'}
        </Button>

        <Button
          onClick={handleConfirmClick}
          variant="contained"
          disabled={isSubmitting}
          color={confirmColor ?? 'primary'}
          startIcon={
            isSubmitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : options.confirmButtonIcon !== undefined ? (
              options.confirmButtonIcon
            ) : (
              <CheckCircle />
            )
          }
          {...options.confirmButtonProps}
        >
          {options.confirmationText || 'Confirmar'}
        </Button>
      </DialogActions>
    </Dialog>
  )
})

ConfirmDialogInternal.displayName = 'ConfirmDialogInternal'

// Nombre único para evitar conflictos con otras librerías
const AppConfirmContext = createContext<AppConfirmContextType | undefined>(undefined)

// Proveedor de datos
export const AppConfirmProvider: React.FC<AppConfirmProviderProps> = ({
  children,
  defaultOptions = {},
}) => {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<AppConfirmOptions>({})

  const resolveRef = useRef<((value: AppConfirmResult) => void) | null>(null)

  // Limpieza de promesas pendientes al desmontar el componente
  useEffect(() => {
    return () => {
      if (resolveRef.current) {
        resolveRef.current({ confirmed: false })
        resolveRef.current = null
      }
    }
  }, [])

  // Función principal para solicitar confirmación
  const requestConfirm = useCallback(
    (opts?: AppConfirmOptions) => {
      // Si había una confirmación previa pendiente sin resolver, resolverla limpiamente
      if (resolveRef.current) {
        resolveRef.current({ confirmed: false })
        resolveRef.current = null
      }

      return new Promise<AppConfirmResult>((resolve) => {
        setOptions({
          color: 'primary',
          ...defaultOptions,
          ...(opts || {}),
        })
        resolveRef.current = resolve
        setOpen(true)
      })
    },
    [defaultOptions],
  )

  const handleClose = useCallback(() => {
    setOpen(false)
    if (resolveRef.current) {
      resolveRef.current({ confirmed: false })
      resolveRef.current = null
    }
  }, [])

  const handleConfirmSuccess = useCallback((reason: string) => {
    setOpen(false)
    if (resolveRef.current) {
      resolveRef.current({ confirmed: true, reason })
      resolveRef.current = null
    }
  }, [])

  return (
    <AppConfirmContext.Provider
      value={{
        requestConfirm,
        confirm: requestConfirm,
        closeConfirm: handleClose,
      }}
    >
      {children}
      <ConfirmDialogInternal
        open={open}
        options={options}
        onClose={handleClose}
        onConfirm={handleConfirmSuccess}
      />
    </AppConfirmContext.Provider>
  )
}

// --- HOOK EXPORTADO CON NOMBRE ÚNICO ---
export const useAppConfirm = () => {
  const context = useContext(AppConfirmContext)
  if (!context)
    throw new Error('useAppConfirm debe usarse dentro de un AppConfirmProvider')
  return context
}
